import {
  type BitkitColorToken,
  type BitkitIconComponent,
  IconCheckCircle,
  IconCrossCircle,
  IconErrorCircle,
  IconSkipCircle,
} from '@bitrise/bitkit-v2';
import isEmpty from 'lodash/isEmpty';

import { PipelineTestResultData, WorkflowTestResultData } from '@/models/TestResultsData';

import {
  TestCase,
  TestFilter,
  TestFilterFunction,
  TestReport,
  TestStatus,
  TestSuite,
  TestSummary,
} from '../TestResults.types';

const PROPERTY_NAMES = {
  STAGE: 'stage',
  WORKFLOW: 'workflow',
};

function extendWithStageName(stageName: string) {
  return (workflow: WorkflowTestResultData) => ({
    stageName,
    workflow,
  });
}

function prependProperties(extraProperties: { key: string; value: string }[]) {
  return (testSuite: TestSuite): TestSuite => ({
    ...testSuite,
    properties: extraProperties.concat(testSuite.properties || []),
  });
}

export function transformToTestReportFromPipeline(pipelineTestResult: PipelineTestResultData | undefined): TestReport {
  if (!pipelineTestResult) {
    return { testSuites: [] };
  }

  const workflows = !isEmpty(pipelineTestResult.workflows)
    ? pipelineTestResult.workflows.map(extendWithStageName(''))
    : pipelineTestResult.stages.flatMap((stage) => stage.workflows.map(extendWithStageName(stage.name)));
  const testSuites: TestSuite[] = workflows.flatMap(({ stageName, workflow }) => {
    const extraProperties = [
      { key: PROPERTY_NAMES.STAGE, value: stageName },
      { key: PROPERTY_NAMES.WORKFLOW, value: workflow.name },
    ];

    return (workflow.testSuites ?? []).map(prependProperties(extraProperties));
  });

  return { testSuites };
}

export function mapTestCaseStatusToIcon(status: TestStatus): {
  icon: BitkitIconComponent;
  iconColor: BitkitColorToken<'icon'>;
} {
  switch (status) {
    case 'passed':
      return { icon: IconCheckCircle, iconColor: 'icon/positive' };
    case 'failed':
      return { icon: IconCrossCircle, iconColor: 'icon/negative' };
    case 'skipped':
      return { icon: IconSkipCircle, iconColor: 'icon/secondary' };
    case 'error':
      return { icon: IconErrorCircle, iconColor: 'icon/negative' };
  }
}

export function collectTestSuiteIds(suites: TestSuite[]): Set<string> {
  const suiteIds = new Set<string>();

  function traverse(suite: TestSuite) {
    suiteIds.add(suite.id);
    suite.testSuites?.forEach(traverse);
  }

  suites.forEach(traverse);
  return suiteIds;
}

export function createStatusFilter(status: TestFilter): TestFilterFunction {
  return (testCase: TestCase) => {
    if (status === 'all') return true;
    if (status === 'flaky') return Boolean(testCase.flaky);
    return testCase.status === status;
  };
}

export function calculateSummary(report: TestReport): TestSummary {
  function count(suite: TestSuite, predicate: TestFilterFunction): number {
    const caseCount = suite.testCases?.filter(predicate).length || 0;
    const suiteCount = suite.testSuites?.reduce((sum, subSuite) => sum + count(subSuite, predicate), 0) || 0;
    return caseCount + suiteCount;
  }

  function countStatus(status: TestFilter) {
    return (acc: number, suite: TestSuite): number => acc + count(suite, createStatusFilter(status));
  }

  const all = report.testSuites.reduce(countStatus('all'), 0);
  const failed = report.testSuites.reduce(countStatus('failed'), 0);
  const skipped = report.testSuites.reduce(countStatus('skipped'), 0);
  const error = report.testSuites.reduce(countStatus('error'), 0);
  const flaky = report.testSuites.reduce(countStatus('flaky'), 0);
  const passed = all - failed - skipped - error;

  return { all, failed, skipped, passed, error, flaky };
}

export function getConcatenatedStageAndWorkflowNameOf(testCase: TestCase, suites: TestSuite[]): string {
  function containsTestCase(suite: TestSuite): boolean {
    if (suite.testCases?.some((tc) => tc.id === testCase.id)) {
      return true;
    }

    return suite.testSuites?.some((subSuite) => containsTestCase(subSuite)) ?? false;
  }

  for (const topLevelSuite of suites) {
    if (containsTestCase(topLevelSuite)) {
      const stageValue = topLevelSuite.properties?.find((p) => p.key === PROPERTY_NAMES.STAGE)?.value;
      const workflowValue = topLevelSuite.properties?.find((p) => p.key === PROPERTY_NAMES.WORKFLOW)?.value;

      if (stageValue && workflowValue) return `${stageValue} • ${workflowValue}`;
      if (stageValue) return stageValue;
      if (workflowValue) return workflowValue;

      return '';
    }
  }

  return '';
}
